<template>
    <div class="w-full space-y-3">
        <Head title="Supplier Revenue Report"/>
        <div class="flex justify-between">
            <h1 class="mt-2 text-2xl font-bold leading-7 text-gray-900 sm:text-3xl">Supplier Revenue Report</h1>
            <a :href="route('frontend.reports.supplier_revenue_report_export', {
                _query: {
                    supplier_ids: form.suppliers,
                    figure_type: form.figure_type,
                    starts_at: form.starts_at,
                    ends_at: form.ends_at,
                    trading_entity: form.trading_entity
                }})" class="py-3 px-3 border border-transparent shadow-sm text-sm leading-4 rounded-md text-white bg-pitch-purple focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amaze-coral w-40 text-center">Download</a>
        </div>
    </div>
    <br/>
    <div class="grid grid-cols-4 gap-2">
        <div class="relative">
            <label for="search" class="block text-sm font-medium text-gray-700">Search by Supplier</label>
            <v-select name="search" class="mt-3 block w-full bg-white rounded-md shadow-sm focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm" :options="suppliers" label="name" :reduce="suppliers => suppliers.id" v-model="form.suppliers" :close-on-select="false" :clear-search-on-select="false" multiple />
        </div>
        <div class="relative" v-if="trading_details.length > 1">
            <label for="trading-entity" class="block text-sm font-medium text-gray-700">Trading Entity</label>
            <select id="trading-entity" v-model="form.trading_entity"
                class="mt-2 bg-gray-50 border border-gray-300 text-gray-900 sm:text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500">
                <option value="" v-html="'All'"></option>
                <option v-for="(trading_detail, index) in trading_details" :key="index" :value="trading_detail.id" :selected="form.trading_entity === trading_detail.id" v-html="trading_detail.trading_name"></option>
            </select>
        </div>
        <div class="relative">
            <label for="start-date" class="block text-sm font-medium text-gray-700">Start Date</label>
            <input v-model="form.starts_at" name="start-date" :min="today" :max="form.ends_at || ''" type="date" class="mt-2 bg-gray-50 border border-gray-300 text-gray-900 sm:text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 datepicker-input">
        </div>
        <div class="relative">
            <label for="end-date" class="block text-sm font-medium text-gray-700">End Date</label>
            <input v-model="form.ends_at" name="end-date" :min="form.starts_at || today" type="date" class="mt-2 bg-gray-50 border border-gray-300 text-gray-900 sm:text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 datepicker-input">
        </div>
    </div>
    <br/>
    <div class="bg-white py-6 px-4 sm:p-6 shadow mb-6">
        <div class="flex flex-col mt-3 space-y-3">
            <div class="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                <div class="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                    <div class="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                        <table class="min-w-full divide-y divide-gray-200">
                            <thead class="bg-gray-50">
                                <!-- First header row -->
                                <tr>
                                    <th scope="col"></th>
                                    <th scope="col" colspan="4"
                                        class="px-6 py-3 text-xs text-gray-500 uppercase tracking-wider text-center cursor-pointer"
                                        @click="showPriceBreakdown(form.suppliers, 'past_confirmed')">
                                        <span class="flex justify-center items-center w-full">
                                            Past Confirmed <SearchIcon class="text-gray-500 w-4 h-4 ml-1" />
                                        </span>
                                    </th>
                                    <th scope="col" colspan="4"
                                        class="px-6 py-3 text-xs text-gray-500 uppercase tracking-wider text-center cursor-pointer"
                                        @click="showPriceBreakdown(form.suppliers, 'past_unconfirmed')">
                                        <span class="flex justify-center items-center w-full">
                                            Past Unconfirmed <SearchIcon class="text-gray-500 w-4 h-4 ml-1" />
                                        </span>
                                    </th>
                                    <th scope="col" colspan="4"
                                        class="px-6 py-3 text-xs text-gray-500 uppercase tracking-wider text-center cursor-pointer"
                                        @click="showPriceBreakdown(form.suppliers, 'future_confirmed')">
                                        <span class="flex justify-center items-center w-full">
                                            Future Confirmed <SearchIcon class="text-gray-500 w-4 h-4 ml-1" />
                                        </span>
                                    </th>
                                    <th scope="col" colspan="4"
                                        class="px-6 py-3 text-xs text-gray-500 uppercase tracking-wider text-center cursor-pointer"
                                        @click="showPriceBreakdown(form.suppliers, 'future_unconfirmed')">
                                        <span class="flex justify-center items-center w-full">
                                            Future Unconfirmed <SearchIcon class="text-gray-500 w-4 h-4 ml-1" />
                                        </span>
                                    </th>
                                </tr>
                                <!-- Second header row -->
                                <tr>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                        Name
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider cursor-pointer"
                                        @click="showPriceBreakdown(form.suppliers, 'past_confirmed')">
                                        Buy Total
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider cursor-pointer"
                                        @click="showPriceBreakdown(form.suppliers, 'past_confirmed')">
                                        Sell Total
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider cursor-pointer"
                                        @click="showPriceBreakdown(form.suppliers, 'past_confirmed')">
                                        Commission Total
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider cursor-pointer"
                                        @click="showPriceBreakdown(form.suppliers, 'past_confirmed')">
                                        Management Fee Total
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider cursor-pointer"
                                        @click="showPriceBreakdown(form.suppliers, 'past_unconfirmed')">
                                        Buy Total
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider cursor-pointer"
                                        @click="showPriceBreakdown(form.suppliers, 'past_unconfirmed')">
                                        Sell Total
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider cursor-pointer"
                                        @click="showPriceBreakdown(form.suppliers, 'past_unconfirmed')">
                                        Commission Total
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider cursor-pointer"
                                        @click="showPriceBreakdown(form.suppliers, 'past_unconfirmed')">
                                        Management Fee Total
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider cursor-pointer"
                                        @click="showPriceBreakdown(form.suppliers, 'future_confirmed')">
                                        Buy Total
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider cursor-pointer"
                                        @click="showPriceBreakdown(form.suppliers, 'future_confirmed')">
                                        Sell Total
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider cursor-pointer"
                                        @click="showPriceBreakdown(form.suppliers, 'future_confirmed')">
                                        Commission Total
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider cursor-pointer"
                                        @click="showPriceBreakdown(form.suppliers, 'future_confirmed')">
                                        Management Fee Total
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider cursor-pointer"
                                        @click="showPriceBreakdown(form.suppliers, 'future_unconfirmed')">
                                        Buy Total
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider cursor-pointer"
                                        @click="showPriceBreakdown(form.suppliers, 'future_unconfirmed')">
                                        Sell Total
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider cursor-pointer"
                                        @click="showPriceBreakdown(form.suppliers, 'future_unconfirmed')">
                                        Commission Total
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider cursor-pointer"
                                        @click="showPriceBreakdown(form.suppliers, 'future_unconfirmed')">
                                        Management Fee Total
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <template v-if="Object.values(data).length > 0">
                                    <tr v-for="(supplier_data, key) in data">
                                        <template v-if="key !== 'totals'">
                                            <td class="whitespace-nowrap py-4 pl-4 pr-4 text-sm font-medium text-gray-900 sm:pl-6">
                                                {{ supplier_data.supplier }}
                                            </td>
                                            <!-- Past confirmed -->
                                            <td class="whitespace-nowrap py-4 pl-4 pr-4 text-sm font-medium text-gray-900 sm:pl-6">
                                                {{ formatCurrency(supplier_data.past_confirmed_buy_total, supplier_data.buy_currency?.code ?? 'GBP', true) }}
                                            </td>
                                            <td class="whitespace-nowrap py-4 pl-4 pr-4 text-sm font-medium text-gray-900 sm:pl-6">
                                                {{ formatCurrency(supplier_data.past_confirmed_sell_total, supplier_data.sell_currency?.code ?? 'GBP', true) }}
                                            </td>
                                            <td class="whitespace-nowrap py-4 pl-4 pr-4 text-sm font-medium text-gray-900 sm:pl-6">
                                                {{ formatCurrency(supplier_data.past_confirmed_commission_total, supplier_data.sell_currency?.code ?? 'GBP', true) }}
                                            </td>
                                            <td class="whitespace-nowrap py-4 pl-4 pr-4 text-sm font-medium text-gray-900 sm:pl-6">
                                                {{ formatCurrency(supplier_data.past_confirmed_management_fee_total, supplier_data.sell_currency?.code ?? 'GBP', true) }}
                                            </td>
                                            <!-- Past unconfirmed -->
                                            <td class="whitespace-nowrap py-4 pl-4 pr-4 text-sm font-medium text-gray-900 sm:pl-6">
                                                {{ formatCurrency(supplier_data.past_unconfirmed_buy_total, supplier_data.buy_currency?.code ?? 'GBP', true) }}
                                            </td>
                                            <td class="whitespace-nowrap py-4 pl-4 pr-4 text-sm font-medium text-gray-900 sm:pl-6">
                                                {{ formatCurrency(supplier_data.past_unconfirmed_sell_total, supplier_data.sell_currency?.code ?? 'GBP', true) }}
                                            </td>
                                            <td class="whitespace-nowrap py-4 pl-4 pr-4 text-sm font-medium text-gray-900 sm:pl-6">
                                                {{ formatCurrency(supplier_data.past_unconfirmed_commission_total, supplier_data.sell_currency?.code ?? 'GBP', true) }}
                                            </td>
                                            <td class="whitespace-nowrap py-4 pl-4 pr-4 text-sm font-medium text-gray-900 sm:pl-6">
                                                {{ formatCurrency(supplier_data.past_unconfirmed_management_fee_total, supplier_data.sell_currency?.code ?? 'GBP', true) }}
                                            </td>
                                            <!-- Future confirmed -->
                                            <td class="whitespace-nowrap py-4 pl-4 pr-4 text-sm font-medium text-gray-900 sm:pl-6">
                                                {{ formatCurrency(supplier_data.future_confirmed_buy_total, supplier_data.buy_currency?.code ?? 'GBP', true) }}
                                            </td>
                                            <td class="whitespace-nowrap py-4 pl-4 pr-4 text-sm font-medium text-gray-900 sm:pl-6">
                                                {{ formatCurrency(supplier_data.future_confirmed_sell_total, supplier_data.sell_currency?.code ?? 'GBP', true) }}
                                            </td>
                                            <td class="whitespace-nowrap py-4 pl-4 pr-4 text-sm font-medium text-gray-900 sm:pl-6">
                                                {{ formatCurrency(supplier_data.future_confirmed_commission_total, supplier_data.sell_currency?.code ?? 'GBP', true) }}
                                            </td>
                                            <td class="whitespace-nowrap py-4 pl-4 pr-4 text-sm font-medium text-gray-900 sm:pl-6">
                                                {{ formatCurrency(supplier_data.future_confirmed_management_fee_total, supplier_data.sell_currency?.code ?? 'GBP', true) }}
                                            </td>
                                            <!-- Future unconfirmed -->
                                            <td class="whitespace-nowrap py-4 pl-4 pr-4 text-sm font-medium text-gray-900 sm:pl-6">
                                                {{ formatCurrency(supplier_data.future_unconfirmed_buy_total, supplier_data.buy_currency?.code ?? 'GBP', true) }}
                                            </td>
                                            <td class="whitespace-nowrap py-4 pl-4 pr-4 text-sm font-medium text-gray-900 sm:pl-6">
                                                {{ formatCurrency(supplier_data.future_unconfirmed_sell_total, supplier_data.sell_currency?.code ?? 'GBP', true) }}
                                            </td>
                                            <td class="whitespace-nowrap py-4 pl-4 pr-4 text-sm font-medium text-gray-900 sm:pl-6">
                                                {{ formatCurrency(supplier_data.future_unconfirmed_commission_total, supplier_data.sell_currency?.code ?? 'GBP', true) }}
                                            </td>
                                            <td class="whitespace-nowrap py-4 pl-4 pr-4 text-sm font-medium text-gray-900 sm:pl-6">
                                                {{ formatCurrency(supplier_data.future_unconfirmed_management_fee_total, supplier_data.sell_currency?.code ?? 'GBP', true) }}
                                            </td>
                                        </template>
                                    </tr>
                                    <!-- Blank Row -->
                                    <tr><td class="whitespace-nowrap py-4 pl-4 pr-4 text-sm font-medium text-gray-900 sm:pl-6"></td></tr>
                                    <tr>
                                        <td class="whitespace-nowrap py-4 pl-4 pr-4 text-sm font-bold text-gray-900 sm:pl-6">
                                            Totals:
                                        </td>
                                        <td class="whitespace-nowrap py-4 pl-4 pr-4 text-sm font-bold text-gray-900 sm:pl-6">
                                            {{ formatCurrency(data['totals'].overall_past_confirmed_buy_total, data['totals'].buy_currency?.code || 'GBP', true) }}
                                        </td>
                                        <td class="whitespace-nowrap py-4 pl-4 pr-4 text-sm font-bold text-gray-900 sm:pl-6">
                                            {{ formatCurrency(data['totals'].overall_past_confirmed_sell_total, data['totals'].sell_currency?.code || 'GBP', true) }}
                                        </td>
                                        <td class="whitespace-nowrap py-4 pl-4 pr-4 text-sm font-bold text-gray-900 sm:pl-6">
                                            {{ formatCurrency(data['totals'].overall_past_confirmed_commission_total, data['totals'].sell_currency?.code || 'GBP', true) }}
                                        </td>
                                        <td class="whitespace-nowrap py-4 pl-4 pr-4 text-sm font-bold text-gray-900 sm:pl-6">
                                            {{ formatCurrency(data['totals'].overall_past_confirmed_management_fee_total, data['totals'].sell_currency?.code || 'GBP', true) }}
                                        </td>
                                        <td class="whitespace-nowrap py-4 pl-4 pr-4 text-sm font-bold text-gray-900 sm:pl-6">
                                            {{ formatCurrency(data['totals'].overall_past_unconfirmed_buy_total, data['totals'].buy_currency?.code || 'GBP', true) }}
                                        </td>
                                        <td class="whitespace-nowrap py-4 pl-4 pr-4 text-sm font-bold text-gray-900 sm:pl-6">
                                            {{ formatCurrency(data['totals'].overall_past_unconfirmed_sell_total, data['totals'].sell_currency?.code || 'GBP', true) }}
                                        </td>
                                        <td class="whitespace-nowrap py-4 pl-4 pr-4 text-sm font-bold text-gray-900 sm:pl-6">
                                            {{ formatCurrency(data['totals'].overall_past_unconfirmed_commission_total, data['totals'].sell_currency?.code || 'GBP', true) }}
                                        </td>
                                        <td class="whitespace-nowrap py-4 pl-4 pr-4 text-sm font-bold text-gray-900 sm:pl-6">
                                            {{ formatCurrency(data['totals'].overall_past_unconfirmed_management_fee_total, data['totals'].sell_currency?.code || 'GBP', true) }}
                                        </td>
                                        <td class="whitespace-nowrap py-4 pl-4 pr-4 text-sm font-bold text-gray-900 sm:pl-6">
                                            {{ formatCurrency(data['totals'].overall_future_confirmed_buy_total, data['totals'].buy_currency?.code || 'GBP', true) }}
                                        </td>
                                        <td class="whitespace-nowrap py-4 pl-4 pr-4 text-sm font-bold text-gray-900 sm:pl-6">
                                            {{ formatCurrency(data['totals'].overall_future_confirmed_sell_total, data['totals'].sell_currency?.code || 'GBP', true) }}
                                        </td>
                                        <td class="whitespace-nowrap py-4 pl-4 pr-4 text-sm font-bold text-gray-900 sm:pl-6">
                                            {{ formatCurrency(data['totals'].overall_future_confirmed_commission_total, data['totals'].sell_currency?.code || 'GBP', true) }}
                                        </td>
                                        <td class="whitespace-nowrap py-4 pl-4 pr-4 text-sm font-bold text-gray-900 sm:pl-6">
                                            {{ formatCurrency(data['totals'].overall_future_confirmed_management_fee_total, data['totals'].sell_currency?.code || 'GBP', true) }}
                                        </td>
                                        <td class="whitespace-nowrap py-4 pl-4 pr-4 text-sm font-bold text-gray-900 sm:pl-6">
                                            {{ formatCurrency(data['totals'].overall_future_unconfirmed_buy_total, data['totals'].buy_currency?.code || 'GBP', true) }}
                                        </td>
                                        <td class="whitespace-nowrap py-4 pl-4 pr-4 text-sm font-bold text-gray-900 sm:pl-6">
                                            {{ formatCurrency(data['totals'].overall_future_unconfirmed_sell_total, data['totals'].sell_currency?.code || 'GBP', true) }}
                                        </td>
                                        <td class="whitespace-nowrap py-4 pl-4 pr-4 text-sm font-bold text-gray-900 sm:pl-6">
                                            {{ formatCurrency(data['totals'].overall_future_unconfirmed_commission_total, data['totals'].sell_currency?.code || 'GBP', true) }}
                                        </td>
                                        <td class="whitespace-nowrap py-4 pl-4 pr-4 text-sm font-bold text-gray-900 sm:pl-6">
                                            {{ formatCurrency(data['totals'].overall_future_unconfirmed_management_fee_total, data['totals'].sell_currency?.code || 'GBP', true) }}
                                        </td>
                                    </tr>
                                </template>
                                <template v-else>
                                    <tr>
                                        <td class="whitespace-nowrap py-4 pl-4 pr-4 text-sm font-medium text-gray-900 sm:pl-6">
                                            No results found.
                                        </td>
                                    </tr>
                                </template>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    </div>
    <SupplierRevenueBreakdown ref="slideout"/>
</template>

<script>
    import axios from 'axios';
    import moment from "moment/moment";
    import pickBy from "lodash/pickBy";
    import debounce from "lodash/debounce";
    import mapValues from "lodash/mapValues";
    import {SearchIcon} from "@heroicons/vue/outline";
    import Authenticated from "@/Layouts/Authenticated";
    import {Link, Head, useForm} from "@inertiajs/vue3";
    import SupplierRevenueBreakdown from "@/Components/SlideOuts/SupplierRevenueBreakdown";

    export default {
        layout: Authenticated,
        components: {
            Head,
            Link,
            SearchIcon,
            SupplierRevenueBreakdown
        },
        props:[
            'suppliers',
            'chosen_suppliers',
            'starts_at',
            'ends_at',
            'data',
            'trading_details'
        ],
        data(props) {
            const chosenSuppliers = Array.isArray(this.chosen_suppliers)
                ? this.chosen_suppliers
                : (this.chosen_suppliers && typeof this.chosen_suppliers === 'object')
                    ? Object.values(this.chosen_suppliers)
                    : (this.chosen_suppliers !== null && this.chosen_suppliers !== undefined && this.chosen_suppliers !== '')
                        ? [this.chosen_suppliers]
                        : [];

            return {
                form: useForm({
                    suppliers: chosenSuppliers.map((supplier) => (supplier && typeof supplier === 'object') ? supplier.id : supplier),
                    starts_at: this.starts_at,
                    ends_at: this.ends_at,
                    trading_entity: null
                })
            }
        },
        methods: {
            reset() {
                this.form = mapValues(this.form, () => null)
            },
            showPriceBreakdown(supplierIds, figureType) {
                // Instantiate empty array
                this.supplierBreakdown = [];

                // Fetch the breakdown data
                axios.post(route('frontend.reports.supplier_revenue_breakdown'), {
                    supplier_ids: supplierIds,
                    figure_type: figureType,
                    starts_at: this.form.starts_at,
                    ends_at: this.form.ends_at,
                    trading_entity: this.form.trading_entity,
                }).then(response => {
                    this.supplierBreakdown = typeof response.data === 'string'
                        ? JSON.parse(response.data)
                        : response.data;

                    // Open slideout
                    this.$refs.slideout.open(this.supplierBreakdown);
                });
            }
        },
        created() {
            this.moment = moment
        },
        watch: {
            form: {
                deep: true,
                handler: debounce(function () {
                    this.$inertia.get(route('frontend.reports.supplier_revenue_report'), pickBy(this.form), {preserveState: true, preserveScroll: true})
                }, 500),
            },
        }
    }
</script>
